import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { FaEdit, FaTrash, FaPlus, FaCheck, FaTimes, FaEnvelope, FaPhone } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { 
  useGetFacultyQuery, 
  useCreateFacultyMutation,
  useUpdateFacultyMutation,
  useDeleteFacultyMutation
} from '../slices/facultyApiSlice';
import { useGetDepartmentsQuery } from '../slices/departmentsApiSlice';
import { useGetSubjectsQuery } from '../slices/subjectsApiSlice';
import { motion } from 'framer-motion';
import Loader from '../components/Loader';

const FacultyManagementScreen = () => {
  const { isDarkMode } = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    departments: [],
    subjects: [],
    designation: '',
    customDesignation: '',
    phone: '',
    password: '',
    isActive: true
  });

  // Add state for department and subject search
  const [departmentSearch, setDepartmentSearch] = useState('');
  const [subjectSearch, setSubjectSearch] = useState('');
  
  const { data: faculty, isLoading, refetch, error } = useGetFacultyQuery();
  const { data: departments } = useGetDepartmentsQuery();
  const { data: subjects } = useGetSubjectsQuery();
  
  // Effect to update subject selections when departments change
  useEffect(() => {
    if (subjects && formData.departments.length > 0) {
      // Keep only subjects that belong to the selected departments
      const validSubjects = formData.subjects.filter(subjectId => {
        const subject = subjects.find(s => s._id === subjectId);
        return subject && (
          formData.departments.includes(subject.department?._id || subject.department)
        );
      });
      
      // If some subjects were filtered out, update the form data
      if (validSubjects.length !== formData.subjects.length) {
        setFormData(prev => ({
          ...prev,
          subjects: validSubjects
        }));
      }
    }
  }, [formData.departments, subjects]);
  
  // Filter departments and subjects based on search
  const filteredDepartments = departments
    ? departments.filter((department) =>
        department.name.toLowerCase().includes(departmentSearch.toLowerCase())
      )
    : [];

  const filteredSubjects = subjects
    ? subjects
        .filter((subject) => {
          // First filter by search term
          const matchesSearch = subject.name
            .toLowerCase()
            .includes(subjectSearch.toLowerCase()) || 
            (subject.code && subject.code.toLowerCase().includes(subjectSearch.toLowerCase()));
          
          // Then filter by selected departments (if any are selected)
          const matchesDepartment = formData.departments.length === 0 || 
            formData.departments.includes(subject.department?._id || subject.department);
          
          return matchesSearch && matchesDepartment;
        })
    : [];
  
  const [createFaculty, { isLoading: isCreating }] = useCreateFacultyMutation();
  const [updateFaculty, { isLoading: isUpdating }] = useUpdateFacultyMutation();
  const [deleteFaculty, { isLoading: isDeleting }] = useDeleteFacultyMutation();

  // Add state for error dialog
  const [errorDialog, setErrorDialog] = useState({
    show: false,
    message: '',
    title: '',
    errorDetails: null
  });

  // Add state to track if showing detailed debug info
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  // Function to show error dialog
  const showErrorDialog = (title, message, errorDetails = null) => {
    setErrorDialog({
      show: true,
      title,
      message,
      errorDetails
    });
  };

  // Function to hide error dialog
  const hideErrorDialog = () => {
    setErrorDialog({
      show: false,
      title: '',
      message: '',
      errorDetails: null
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      departments: [],
      subjects: [],
      designation: '',
      customDesignation: '',
      phone: '',
      password: '',
      isActive: true
    });
    setSelectedFaculty(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
    setDepartmentSearch('');
    setSubjectSearch('');
  };

  const handleCreateClick = () => {
    setModalMode('create');
    resetForm();
    setDepartmentSearch('');
    setSubjectSearch('');
    setShowModal(true);
  };

  const handleEditClick = (faculty) => {
    setModalMode('edit');
    setSelectedFaculty(faculty);
    
    // List of predefined designations
    const predefinedDesignations = [
      "Head of Department (HOD)",
      "Professor", 
      "Associate Professor", 
      "Assistant Professor", 
      "Senior Lecturer", 
      "Lecturer", 
      "Junior Lecturer", 
      "Visiting Faculty", 
      "Adjunct Faculty", 
      "Lab Assistant", 
      "Teaching Assistant", 
      "Research Associate"
    ];
    
    // Check if the designation is one of the predefined ones
    const designation = faculty.designation || '';
    const isCustomDesignation = designation && !predefinedDesignations.includes(designation);
    
    setFormData({
      name: faculty.name,
      email: faculty.email,
      departments: faculty.departments?.map(d => d._id) || [],
      subjects: faculty.subjects?.map(s => s._id) || [],
      designation: isCustomDesignation ? 'Other' : (designation || ''),
      customDesignation: isCustomDesignation ? designation : '',
      phone: faculty.phone || '',
      isActive: faculty.isActive
    });
    
    setDepartmentSearch('');
    setSubjectSearch('');
    setShowModal(true);
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm('Are you sure you want to delete this faculty member? This action cannot be undone.')) {
      try {
        await deleteFaculty(id).unwrap();
        toast.success('Faculty deleted successfully');
        refetch();
      } catch (error) {
        toast.error(error?.data?.message || 'Failed to delete faculty');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare submission data
    const submissionData = {
      ...formData
    };
    
    // If "Other" is selected as designation, use the customDesignation value
    if (formData.designation === 'Other' && formData.customDesignation) {
      submissionData.designation = formData.customDesignation;
    }
    
    // Remove customDesignation as it's not part of the backend model
    delete submissionData.customDesignation;
    
    try {
      if (modalMode === 'create') {
        const response = await createFaculty(submissionData).unwrap();
        console.log('Faculty creation response:', response);
        
        // Only show success if we received a valid response
        if (response && response._id) {
          toast.success('Faculty created successfully');
          handleCloseModal();
          refetch();
        } else {
          // Handle partial success - backend created the record but returned unexpected response
          console.warn('Faculty might have been created but received unexpected response:', response);
          toast.success('Faculty may have been created successfully. Refreshing data...');
          handleCloseModal();
          setTimeout(() => refetch(), 500); // Delay refetch to allow backend to complete processing
        }
      } else {
        const response = await updateFaculty({
          id: selectedFaculty._id,
          ...submissionData
        }).unwrap();
        
        console.log('Faculty update response:', response);
        toast.success('Faculty updated successfully');
        handleCloseModal();
        refetch();
      }
    } catch (err) {
      console.error('Error during faculty operation:', err);
      
      // Analyze the error thoroughly to determine what happened
      const errorAnalysis = analyzeBackendError(err);
      
      // Check if the error indicates a possible successful operation despite the error
      if (err?.data?.possibleSuccess) {
        console.warn('Server error but faculty might be created:', err);
        toast.success(err.data.message || 'Faculty may have been created. Please check the faculty list.');
        handleCloseModal();
        
        // Refresh the data after a brief delay
        setTimeout(() => {
          refetch();
          // Also redirect to another page and back to force a clean reload
          window.location.href = '/institute/dashboard';
          setTimeout(() => {
            window.location.href = '/institute/faculty';
          }, 100);
        }, 500);
        return;
      }
      
      // Handle duplicate key errors (which could be coming from various error patterns)
      if (errorAnalysis.isDuplicate) {
        console.log('Detected duplicate email error based on error analysis:', errorAnalysis);
        
        // Instead of showing dialog, we can directly check if this faculty is showing in the list
        const email = submissionData.email;
        const existingFaculty = faculty?.find(f => f.email === email && f._id !== selectedFaculty?._id);
        
        if (existingFaculty) {
          // If we found the faculty in the current data, it means creation was actually successful or a duplicate exists
          if (modalMode === 'create') {
            toast.success('Faculty already exists. Redirecting to view the existing record.');
            handleCloseModal();
            setTimeout(() => window.location.reload(), 500);
          } else {
            // For edit mode, show specific error about duplicate email
            toast.error(`Cannot update email: A faculty member with email ${email} already exists`);
          }
        } else {
          // Show the dialog with detailed error information
          showErrorDialog(
            modalMode === 'create' ? 'Duplicate Email' : 'Email Update Failed',
            `A faculty member with this email (${submissionData.email}) already exists in the database. Please use a different email address.`,
            errorAnalysis.errorDetails
          );
        }
      } 
      // Check if there's a validation error
      else if (err?.data?.message?.includes('validation')) {
        toast.error(`Validation error: ${err?.data?.message || 'Please check your inputs.'}`);
      }
      // Check if there's a custom message from backend
      else if (err?.data?.message) {
        toast.error(err.data.message);
      } 
      // If there's no error message but status code is 201 (Created), it might be a false error
      else if (err.status === 201 || (err?.data?.success === true)) {
        console.warn('Received status with error but success flag is true, treating as success:', err);
        toast.success('Faculty created successfully');
        handleCloseModal();
        setTimeout(() => refetch(), 500);
      }
      // Handle 500 errors specially
      else if (err.status === 500) {
        console.warn('Received 500 server error, faculty may have been created anyway:', err);
        
        // Check if the error details suggest a backend coding issue
        if (errorAnalysis.errorDetails?.includes('TypeError') || 
            errorAnalysis.errorDetails?.includes('SyntaxError') ||
            errorAnalysis.errorDetails?.includes('ReferenceError')) {
          
          // Show a more specific error about potential backend issues
          toast.error('Server error occurred: Possible backend code issue. Please contact the developer.');
          
          // Show detailed error dialog for the user
          showErrorDialog(
            'Backend Error Detected', 
            'A server-side error occurred that suggests there might be an issue with the backend code. ' +
            'This is usually not your fault as a user. An administrator should check the server logs.',
            errorAnalysis.errorDetails
          );
        } else {
          toast.error('Server error occurred, but faculty may have been created. Please check the faculty list.');
          handleCloseModal();
          setTimeout(() => refetch(), 500);
        }
      }
      else {
        // Generic error message
        toast.error('Failed to save faculty. Please try again.');
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'departments' || name === 'subjects') {
      const options = Array.from(e.target.selectedOptions).map(option => option.value);
      setFormData({
        ...formData,
        [name]: options
      });
    } else if (name === 'designation') {
      // Reset customDesignation if something other than 'Other' is selected
      setFormData({
        ...formData,
        designation: value,
        customDesignation: value === 'Other' ? formData.customDesignation : ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1, 
      transition: { duration: 0.3 }
    }
  };

  // Add utility function to analyze backend errors
  const analyzeBackendError = (err) => {
    console.group('Backend Error Analysis');
    console.log('Error object:', err);
    
    // Check status code
    console.log('Status code:', err?.status);
    
    // Check error data
    if (err?.data) {
      console.log('Error data:', err.data);
      console.log('Error message:', err.data.message);
      
      // If there's a stack trace
      if (err.data.stack) {
        console.log('Stack trace provided by backend:');
        console.log(err.data.stack);
      }
    }
    
    // Check original error if passed through
    if (err?.data?.originalError) {
      console.log('Original error from server:', err.data.originalError);
    }
    
    // Check for MongoDB duplicate key error pattern
    const errorText = JSON.stringify(err);
    if (errorText.includes('E11000')) {
      console.log('MongoDB duplicate key error detected (E11000)');
      
      // Extract the duplicate key field if possible
      const duplicateKeyMatch = errorText.match(/E11000 duplicate key error.*?{(.+?):/);
      if (duplicateKeyMatch && duplicateKeyMatch[1]) {
        console.log('Duplicate field: ', duplicateKeyMatch[1].trim());
      }
    }
    
    console.groupEnd();
    
    // Return analysis results
    return {
      isDuplicate: errorText.includes('E11000') || 
                  errorText.includes('duplicate key') || 
                  errorText.includes('already exists') ||
                  err?.status === 409,
      errorDetails: err?.data?.errorDetails || errorText
    };
  };

  if (isLoading) return (
    <div className="flex justify-center items-center h-screen">
      <Loader size="medium" text="Loading faculty data..." />
    </div>
  );

  if (error) return (
    <div className="text-red-500 text-center p-4">
      {error?.data?.message || 'Failed to load faculty members'}
    </div>
  );

  return (
    <motion.div 
      className={`p-6 pt-24 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'} min-h-screen`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Faculty Management</h1>
          <button
            onClick={handleCreateClick}
            className="flex items-center px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
          >
            <FaPlus className="mr-2" />
            Add Faculty
          </button>
        </div>

        {faculty?.length === 0 ? (
          <div className={`text-center p-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow`}>
            <p>No faculty members found. Create your first faculty member to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {faculty?.map((f) => (
              <div 
                key={f._id} 
                className={`rounded-lg shadow-md p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
              >
                <div className="flex justify-between">
                  <h2 className="text-xl font-semibold">{f.name}</h2>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditClick(f)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(f._id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
                
                <div className="mt-2 text-sm">
                  <p className="flex items-center mb-1">
                    <FaEnvelope className="mr-2" />
                    {f.email}
                  </p>
                  {f.phone && (
                    <p className="flex items-center mb-1">
                      <FaPhone className="mr-2" />
                      {f.phone}
                    </p>
                  )}
                  {f.designation && (
                    <p className="mt-1 font-medium">{f.designation}</p>
                  )}
                </div>
                
                <div className="mt-4">
                  <p className={`text-xs uppercase font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Departments
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {f.departments && f.departments.length > 0 ? (
                      f.departments.map(dept => (
                        <span 
                          key={dept._id} 
                          className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800"
                        >
                          {dept.name}
                        </span>
                      ))
                    ) : (
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        No departments assigned
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="mt-4">
                  <p className={`text-xs uppercase font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Subjects
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {f.subjects && f.subjects.length > 0 ? (
                      f.subjects.map(subj => (
                        <span 
                          key={subj._id} 
                          className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800"
                        >
                          {subj.name}
                        </span>
                      ))
                    ) : (
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        No subjects assigned
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="mt-4 flex justify-end">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    f.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {f.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-500'} opacity-75`}></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className={`inline-block align-bottom ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full`}>
                <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} px-4 pt-5 pb-4 sm:p-6 sm:pb-4`}>
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium">
                        {modalMode === 'create' ? 'Create Faculty' : 'Edit Faculty'}
                      </h3>
                      <div className="mt-4">
                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium">
                              Name
                            </label>
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                              className={`mt-1 block w-full py-2 px-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500`}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium">
                              Email
                            </label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              className={`mt-1 block w-full py-2 px-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500`}
                            />
                            {modalMode === 'edit' && (
                              <p className="mt-1 text-xs text-violet-500">
                                You can change the email address. This will update both faculty and user accounts.
                              </p>
                            )}
                          </div>
                          
                          {modalMode === 'create' && (
                            <div>
                              <label className="block text-sm font-medium">
                                Password
                              </label>
                              <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Leave blank for random password"
                                className={`mt-1 block w-full py-2 px-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500`}
                              />
                            </div>
                          )}
                          
                          <div>
                            <label className="block text-sm font-medium">
                              Phone
                            </label>
                            <input
                              type="text"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              className={`mt-1 block w-full py-2 px-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500`}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium">
                              Designation
                            </label>
                            <select
                              name="designation"
                              value={formData.designation}
                              onChange={handleChange}
                              className={`mt-1 block w-full py-2 px-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500`}
                            >
                              <option value="">Select Designation</option>
                              <option value="Head of Department (HOD)">Head of Department (HOD)</option>
                              <option value="Professor">Professor</option>
                              <option value="Associate Professor">Associate Professor</option>
                              <option value="Assistant Professor">Assistant Professor</option>
                              <option value="Senior Lecturer">Senior Lecturer</option>
                              <option value="Lecturer">Lecturer</option>
                              <option value="Junior Lecturer">Junior Lecturer</option>
                              <option value="Visiting Faculty">Visiting Faculty</option>
                              <option value="Adjunct Faculty">Adjunct Faculty</option>
                              <option value="Lab Assistant">Lab Assistant</option>
                              <option value="Teaching Assistant">Teaching Assistant</option>
                              <option value="Research Associate">Research Associate</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          
                          {formData.designation === 'Other' && (
                            <div>
                              <label className="block text-sm font-medium">
                                Custom Designation
                              </label>
                              <input
                                type="text"
                                name="customDesignation"
                                value={formData.customDesignation}
                                onChange={handleChange}
                                className={`mt-1 block w-full py-2 px-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500`}
                              />
                            </div>
                          )}
                          
                          <div>
                            <label className="block text-sm font-medium">
                              Departments
                            </label>
                            <div className="mt-1 mb-2">
                              <input
                                type="text"
                                placeholder="Search departments..."
                                value={departmentSearch}
                                onChange={(e) => setDepartmentSearch(e.target.value)}
                                className={`w-full py-2 px-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500`}
                              />
                            </div>
                            <div className="flex justify-between items-center mb-1">
                              <p className="text-xs">
                                Hold Ctrl/Cmd key to select multiple
                              </p>
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                {formData.departments.length} selected
                              </span>
                            </div>
                            <select
                              name="departments"
                              multiple
                              value={formData.departments}
                              onChange={handleChange}
                              className={`block w-full py-2 px-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500`}
                              size="5"
                            >
                              {filteredDepartments.map((dept) => (
                                <option key={dept._id} value={dept._id}>
                                  {dept.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium">
                              Subjects
                            </label>
                            <div className="mt-1 mb-2">
                              <input
                                type="text"
                                placeholder="Search subjects by name or code..."
                                value={subjectSearch}
                                onChange={(e) => setSubjectSearch(e.target.value)}
                                className={`w-full py-2 px-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500`}
                              />
                            </div>
                            <div className="flex justify-between items-center mb-1">
                              <p className="text-xs">
                                Hold Ctrl/Cmd key to select multiple
                              </p>
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                                {formData.subjects.length} selected
                              </span>
                            </div>
                            <select
                              name="subjects"
                              multiple
                              value={formData.subjects}
                              onChange={handleChange}
                              className={`block w-full py-2 px-3 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-violet-500 focus:border-violet-500`}
                              size="5"
                            >
                              {filteredSubjects.map((subj) => (
                                <option key={subj._id} value={subj._id}>
                                  {subj.name} ({subj.code})
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          {modalMode === 'edit' && (
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                name="isActive"
                                id="isActive"
                                checked={formData.isActive}
                                onChange={handleChange}
                                className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                              />
                              <label htmlFor="isActive" className="ml-2 block text-sm">
                                Active
                              </label>
                            </div>
                          )}
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse`}>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isCreating || isUpdating}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-violet-600 text-base font-medium text-white hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {isCreating || isUpdating ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className={`mt-3 w-full inline-flex justify-center rounded-md border ${isDarkMode ? 'border-gray-600 bg-gray-800 hover:bg-gray-700' : 'border-gray-300 bg-white hover:bg-gray-50'} shadow-sm px-4 py-2 text-base font-medium sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Dialog */}
        {errorDialog.show && (
          <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-500'} opacity-75`}></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className={`inline-block align-bottom ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full`}>
                <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} px-4 pt-5 pb-4 sm:p-6 sm:pb-4`}>
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                        {errorDialog.title}
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-gray-300">
                          {errorDialog.message}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-300 mt-2">
                          Note: If you see this faculty in the list after returning to the dashboard, it means creation was actually successful despite the error.
                        </p>
                        <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900 rounded-md">
                          <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                            Recommended actions:
                          </p>
                          <ul className="list-disc pl-5 mt-1 text-sm text-blue-700 dark:text-blue-300">
                            <li>Use a different email address</li>
                            <li>Click 'OK' to return to the dashboard and try again</li>
                            <li>If you believe this is a system error, contact support</li>
                          </ul>
                        </div>
                        
                        {/* Debug section for developers - only shown if error details available */}
                        {errorDialog.errorDetails && (
                          <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-3">
                            <button
                              type="button"
                              onClick={() => setShowDebugInfo(!showDebugInfo)}
                              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 flex items-center"
                            >
                              <svg 
                                className={`h-4 w-4 mr-1 transform transition-transform ${showDebugInfo ? 'rotate-90' : ''}`} 
                                xmlns="http://www.w3.org/2000/svg" 
                                viewBox="0 0 20 20" 
                                fill="currentColor"
                              >
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              </svg>
                              {showDebugInfo ? 'Hide Debug Info' : 'Show Debug Info (For Developers)'}
                            </button>
                            
                            {showDebugInfo && (
                              <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded overflow-auto max-h-40 text-xs">
                                <pre className="whitespace-pre-wrap break-words text-gray-700 dark:text-gray-300">
                                  {errorDialog.errorDetails}
                                </pre>
                                <div className="mt-2 text-xs text-red-600 dark:text-red-400">
                                  <p>⚠️ This appears to be a backend issue. Please check the server logs and the faculty controller's handling of duplicate emails.</p>
                                  <p className="mt-1">Common issues:</p>
                                  <ul className="list-disc pl-5 mt-1">
                                    <li>MongoDB duplicate key error not being properly caught</li>
                                    <li>Missing try/catch block around user creation</li>
                                    <li>Improper error response formatting</li>
                                  </ul>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse`}>
                  <button
                    type="button"
                    onClick={() => {
                      hideErrorDialog();
                      // Close the form modal too
                      handleCloseModal();
                      // Show a toast message to indicate we're refreshing
                      toast.success('Refreshing faculty list...');
                      // Wait briefly and then navigate back to dashboard to refresh data
                      setTimeout(() => {
                        window.location.href = '/institute/dashboard';
                        // After a brief moment, come back to the faculty screen
                        setTimeout(() => {
                          window.location.href = '/institute/faculty';
                        }, 100);
                      }, 500);
                    }}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-violet-600 text-base font-medium text-white hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 sm:w-auto sm:text-sm"
                  >
                    OK
                  </button>
                  <button
                    type="button"
                    onClick={hideErrorDialog}
                    className={`mt-3 w-full inline-flex justify-center rounded-md border ${isDarkMode ? 'border-gray-600 bg-gray-800 hover:bg-gray-700' : 'border-gray-300 bg-white hover:bg-gray-50'} shadow-sm px-4 py-2 text-base font-medium sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm`}
                  >
                    Continue Editing
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default FacultyManagementScreen; 